/**
 * ColorDots - displays MTG color identity using mana-font symbols
 * WUBRG order: White → Blue → Black → Red → Green
 * Colorless cards show the colorless mana symbol
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';

import { costIconFontSize } from '@/components/ManaSymbol/ManaSymbol';
import type { Color } from '@/types/card';

const COLOR_ORDER: Color[] = ['W', 'U', 'B', 'R', 'G'];

const COLOR_NAMES: Record<Color, string> = {
  W: 'White',
  U: 'Blue',
  B: 'Black',
  R: 'Red',
  G: 'Green',
};

export interface ColorDotsProps {
  colors: Color[];
  /** Desired visible height of each icon pill in px (default: 20) */
  iconSize?: number;
  className?: string;
}

export default function ColorDots({ colors, iconSize = 20, className }: ColorDotsProps): ReactElement {
  const isColorless = colors.length === 0;

  // Build tooltip text
  const tooltipText = isColorless ? 'Colorless' : colors.map((c) => COLOR_NAMES[c]).join(', ');

  // Sort colors in WUBRG order
  const sortedColors = COLOR_ORDER.filter((c) => colors.includes(c));

  return (
    <div
      className={clsx('flex items-center gap-0.5', className)}
      style={{ fontSize: costIconFontSize(iconSize) }}
      title={tooltipText}
      aria-label={tooltipText}
    >
      {isColorless ? (
        <i className="ms ms-c ms-cost" aria-hidden="true" />
      ) : (
        sortedColors.map((color) => <i key={color} className={clsx('ms', `ms-${color.toLowerCase()}`, 'ms-cost')} aria-hidden="true" />)
      )}
    </div>
  );
}
